import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as XLSX from "xlsx";

import { batchList } from "../../../api/Batch";
import {
  batchEnrolledStudentsV0,
  studentBulkFeeUpdate,
} from "../../../api/StudentImport";

import { SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";

/* ============================================================
   HELPERS
============================================================ */

const money = (value) => {
  const num = Number(value || 0);

  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const num = (value) => {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
};

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeRegistration = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const normalizeFeeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

/*
|--------------------------------------------------------------------------
| Student Search Normalizer
|--------------------------------------------------------------------------
|
| Removes spaces, brackets, hyphens etc.
| This makes searches like:
|
| 97694 64425
| 9769464425
|
| work the same way.
|
*/

const normalizeSearchValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

/* ============================================================
   COMPONENT
============================================================ */

export default function StudentFeeUpdateV1() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");

  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");

  /*
   ==========================================================
   ALL STUDENTS LOADED FROM API

   This state always contains the complete list.

   Search/filter does NOT modify this state.
   ==========================================================
  */

  const [students, setStudents] = useState([]);

  /*
   ==========================================================
   STUDENT SEARCH
   ==========================================================
  */

  const [studentSearch, setStudentSearch] =
    useState("");

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [showPreviousFee, setShowPreviousFee] =
    useState(true);

  const [showAdmissionFee, setShowAdmissionFee] =
    useState(true);

  const [updateStatus, setUpdateStatus] =
    useState({});

  /*
   ==========================================================
   ORIGINAL DATA SNAPSHOT
  ==========================================================
  */

  const originalStudentsRef = useRef({});

  /*
   Excel input
  */

  const excelInputRef = useRef(null);

  const [excelLoading, setExcelLoading] =
    useState(false);

  const [excelMatched, setExcelMatched] =
    useState(0);

  const [excelNotMatched, setExcelNotMatched] =
    useState([]);

  /* ==========================================================
     APPLY MISSING BATCH FEES MODAL
     ========================================================== */
  const [showBatchFeeModal, setShowBatchFeeModal] = useState(false);
  const [selectedBatchFeeIds, setSelectedBatchFeeIds] = useState([]);
  const [batchFeeApplyLoading, setBatchFeeApplyLoading] = useState(false);

  /* ==========================================================
     INITIAL SESSION
  ========================================================== */

  useEffect(() => {
    const storedSessions = JSON.parse(
      localStorage.getItem("sessions") ?? "[]"
    );

    setSessions(storedSessions);

    const activeSession =
      storedSessions.find(
        (s) => String(s.status) === "1"
      ) || storedSessions[0];

    if (activeSession?.id) {
      setSessionId(String(activeSession.id));

      getBatchList(activeSession.id);
    }
  }, []);

  /* ==========================================================
     GET BATCHES
  ========================================================== */

  const getBatchList = async (sid) => {
    if (!sid) return;

    setLoading(true);

    try {
      const response = await batchList(
        `limit=all&session_id=${sid}`
      );

      if (response?.data?.status) {
        setBatches(response.data.data || []);
      } else {
        setBatches([]);

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message ||
            "Failed to load batches"
        );
      }
    } catch (error) {
      setBatches([]);

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load batches"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     SESSION CHANGE
  ========================================================== */

  const handleSessionChange = async (value) => {
    setSessionId(value);
    setBatchId("");

    /*
     Clear student search when session changes.
    */
    setStudentSearch("");

    setStudents([]);
    setUpdateStatus({});

    originalStudentsRef.current = {};

    await getBatchList(value);
  };

  /* ==========================================================
     BATCH CHANGE
  ========================================================== */

  const handleBatchChange = async (value) => {
    setBatchId(value);

    /*
     Clear student search when batch changes.
    */
    setStudentSearch("");

    setStudents([]);
    setUpdateStatus({});

    originalStudentsRef.current = {};

    if (!value || !sessionId) return;

    await fetchStudents(value);
  };

  /* ==========================================================
     PREPARE STUDENT
  ========================================================== */

  const prepareStudent = (student) => {
    /*
     * IMPORTANT:
     * The normal table must show ONLY fees actually stored/applied
     * in student_fee_details. Batch fees are NOT auto-injected here.
     * Missing batch fees are applied only through the verification modal.
     */
    let fees = (student?.student_fee_details || []).map((existingFee) => {
      const actualAmount = num(existingFee.actual_amount);
      const paidAmount = num(existingFee.paid_amount);
      const finalAmount = Math.max(num(existingFee.final_amount), paidAmount);

      return {
        student_fee_id: existingFee.id || null,
        batch_fee_id: existingFee.batch_fee_id || null,
        fee_type: existingFee.fee_type,
        actual_amount: actualAmount,
        paid_amount: paidAmount,
        discount:
          existingFee.discount !== undefined
            ? num(existingFee.discount)
            : Math.max(actualAmount - finalAmount, 0),
        final_amount: finalAmount,
        due_amount:
          existingFee.due_amount !== undefined
            ? Math.max(num(existingFee.due_amount), 0)
            : Math.max(finalAmount - paidAmount, 0),
        is_batch_fee: !!existingFee.batch_fee_id,
        is_existing: true,
      };
    });

    if (!showAdmissionFee) {
      fees = fees.filter((fee) => {
        const key = normalizeFeeKey(fee.fee_type);
        return key !== "registration fee" && key !== "admission fee";
      });
    }

    return {
      ...student,
      rte_status: String(student.rte_status || "NON-RTE").toUpperCase(),
      fees,
      previous_session_due_fees: num(student.previous_session_due_fees || 0),
      previous_session_due_fees_remark:
        student.previous_session_due_fees_remark || "",
      update_status: null,
      update_message: "",
    };
  };

  /* ==========================================================
     FETCH STUDENTS
  ========================================================== */

  const fetchStudents = async (
    selectedBatchId
  ) => {
    setLoading(true);

    try {
      const response =
        await batchEnrolledStudentsV0(
          `batch_id=${selectedBatchId}&session_id=${sessionId}`
        );

      if (!response?.data?.status) {
        setStudents([]);

        originalStudentsRef.current = {};

        return SweetAlertShow(
          "info",
          "No Students",
          response?.data?.message ||
            "No enrolled students found."
        );
      }

      const data =
        response?.data?.data || [];

      if (!data.length) {
        setStudents([]);

        originalStudentsRef.current = {};

        return SweetAlertShow(
          "info",
          "No Students",
          "No enrolled students found."
        );
      }

      const prepared = data.map(
        (student) =>
          prepareStudent(student)
      );

      setStudents(prepared);

      /*
       Clear search after new API data.
      */
      setStudentSearch("");

      /*
       SAVE ORIGINAL SNAPSHOT
      */

      const snapshot = {};

      prepared.forEach((student) => {
        snapshot[student.id] =
          createComparableStudent(
            student
          );
      });

      originalStudentsRef.current =
        snapshot;

      setUpdateStatus({});
      setExcelMatched(0);
      setExcelNotMatched([]);
    } catch (error) {
      setStudents([]);

      originalStudentsRef.current = {};

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load students"
      );
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     STUDENT FILTER
  ========================================================== */

  const filteredStudents = useMemo(() => {
    const search =
      normalizeSearchValue(
        studentSearch
      );

    /*
     No search = return all students.

     We also keep the ORIGINAL ARRAY INDEX.
     This is very important because your existing
     handlers use studentIndex.
    */

    if (!search) {
      return students.map(
        (student, index) => ({
          student,
          originalIndex: index,
        })
      );
    }

    return students
      .map((student, index) => ({
        student,
        originalIndex: index,
      }))
      .filter(
        ({
          student,
        }) => {
          const name =
            normalizeSearchValue(
              student?.student?.name
            );

          const phone =
            normalizeSearchValue(
              student?.student?.phone
            );

          const registrationNo =
            normalizeSearchValue(
              student?.student
                ?.registration_no
            );

          const rollNumber =
            normalizeSearchValue(
              student?.roll_number
            );

          /*
           Search against:
           - Name
           - Mobile
           - Registration
           - Roll number
          */

          return (
            name.includes(search) ||
            phone.includes(search) ||
            registrationNo.includes(
              search
            ) ||
            rollNumber.includes(search)
          );
        }
      );
  }, [
    students,
    studentSearch,
  ]);

  /* ==========================================================
     CLEAR STUDENT SEARCH
  ========================================================== */

  const clearStudentSearch = () => {
    setStudentSearch("");
  };

  /* ==========================================================
     PREPARE COMPARABLE STUDENT
  ========================================================== */

  const createComparableStudent = (
    student
  ) => {
    return {
      rte_status:
        String(
          student.rte_status ||
            "NON-RTE"
        ).toUpperCase(),

      previous_session_due_fees:
        num(
          student.previous_session_due_fees
        ),

      previous_session_due_fees_remark:
        String(
          student.previous_session_due_fees_remark ||
            ""
        ),

      fees: (student.fees || [])
        .map((fee) => ({
          id:
            fee.student_fee_id ||
            null,

          batch_fee_id:
            fee.batch_fee_id ||
            null,

          fee_type:
            fee.fee_type,

          final_amount:
            num(fee.final_amount),
        }))
        .sort((a, b) =>
          String(a.fee_type).localeCompare(
            String(b.fee_type)
          )
        ),
    };
  };

  /* ==========================================================
     IS STUDENT DIRTY
  ========================================================== */

  const isStudentDirty = (student) => {
    const original =
      originalStudentsRef.current[
        student.id
      ];

    if (!original) {
      return true;
    }

    const current =
      createComparableStudent(
        student
      );

    return (
      JSON.stringify(current) !==
      JSON.stringify(original)
    );
  };

  /* ==========================================================
     DIRTY STUDENTS
  ========================================================== */

  const dirtyStudents = useMemo(() => {
    return students.filter(
      (student) =>
        isStudentDirty(student)
    );
  }, [students]);

  /* ==========================================================
     TOTAL PAID
  ========================================================== */

  const getTotalPaid = (student) => {
    return (student.fees || []).reduce(
      (sum, fee) =>
        sum + num(fee.paid_amount),
      0
    );
  };

  /* ==========================================================
     CALCULATE TOTALS
  ========================================================== */

  const calculateTotals = (
    student
  ) => {
    const actual =
      (student.fees || []).reduce(
        (sum, fee) =>
          sum +
          num(fee.actual_amount),
        0
      );

    const paid =
      (student.fees || []).reduce(
        (sum, fee) =>
          sum +
          num(fee.paid_amount),
        0
      );

    const discount =
      (student.fees || []).reduce(
        (sum, fee) =>
          sum + num(fee.discount),
        0
      );

    const payable =
      (student.fees || []).reduce(
        (sum, fee) =>
          sum +
          num(fee.final_amount),
        0
      );

    const due =
      (student.fees || []).reduce(
        (sum, fee) =>
          sum + num(fee.due_amount),
        0
      );

    const previousFee =
      showPreviousFee
        ? num(
            student.previous_session_due_fees
          )
        : 0;

    return {
      actual,
      paid,
      discount,
      payable,
      due,
      previousFee,
      grandDue:
        due + previousFee,
    };
  };

  /* ==========================================================
     RTE CHANGE
  ========================================================== */

  const handleRteChange = (
    studentIndex,
    value
  ) => {
    const student =
      students[studentIndex];

    if (!student) return;

    const totalPaid =
      getTotalPaid(student);

    if (
      totalPaid > 0 &&
      value !== student.rte_status
    ) {
      SweetAlertShow(
        "warning",
        "RTE Not Allowed",
        `₹${money(
          totalPaid
        )} has already been paid. RTE status cannot be changed.`
      );

      return;
    }

    setStudents((prev) =>
      prev.map(
        (item, index) => {
          if (
            index !==
            studentIndex
          ) {
            return item;
          }

          const updatedFees =
            item.fees.map(
              (fee) => {
                const actual =
                  num(
                    fee.actual_amount
                  );

                const paid =
                  num(
                    fee.paid_amount
                  );

                if (
                  value === "RTE"
                ) {
                  if (paid > 0) {
                    return fee;
                  }

                  return {
                    ...fee,

                    final_amount:
                      0,

                    discount:
                      actual,

                    due_amount:
                      0,
                  };
                }

                const restoredFinal =
                  Math.max(
                    actual,
                    paid
                  );

                return {
                  ...fee,

                  final_amount:
                    restoredFinal,

                  discount:
                    Math.max(
                      actual -
                        restoredFinal,
                      0
                    ),

                  due_amount:
                    Math.max(
                      restoredFinal -
                        paid,
                      0
                    ),
                };
              }
            );

          return {
            ...item,

            rte_status:
              value,

            fees:
              updatedFees,
          };
        }
      )
    );
  };

  /* ==========================================================
     MARK ALL RTE
  ========================================================== */

  const markAllRTE = () => {
    if (!students.length) {
      return;
    }

    setStudents((prev) =>
      prev.map((student) => {
        const totalPaid =
          getTotalPaid(student);

        if (totalPaid > 0) {
          return student;
        }

        const updatedFees =
          student.fees.map(
            (fee) => {
              const actual =
                num(
                  fee.actual_amount
                );

              const paid =
                num(
                  fee.paid_amount
                );

              if (paid > 0) {
                return fee;
              }

              return {
                ...fee,

                final_amount: 0,

                discount: actual,

                due_amount: 0,
              };
            }
          );

        return {
          ...student,

          rte_status: "RTE",

          fees: updatedFees,
        };
      })
    );

    SweetAlertShow(
      "success",
      "RTE Marked",
      "All eligible unpaid students have been marked as RTE. Click Update All to save."
    );
  };

  /* ==========================================================
     MARK ALL NON-RTE
  ========================================================== */

  const markAllNonRTE = () => {
    if (!students.length) {
      return;
    }

    setStudents((prev) =>
      prev.map((student) => {
        const totalPaid =
          getTotalPaid(student);

        if (totalPaid > 0) {
          return student;
        }

        const updatedFees =
          student.fees.map(
            (fee) => {
              const actual =
                num(
                  fee.actual_amount
                );

              const paid =
                num(
                  fee.paid_amount
                );

              const finalAmount =
                Math.max(
                  actual,
                  paid
                );

              return {
                ...fee,

                final_amount:
                  finalAmount,

                discount:
                  Math.max(
                    actual -
                      finalAmount,
                    0
                  ),

                due_amount:
                  Math.max(
                    finalAmount -
                      paid,
                    0
                  ),
              };
            }
          );

        return {
          ...student,

          rte_status:
            "NON-RTE",

          fees:
            updatedFees,
        };
      })
    );

    SweetAlertShow(
      "info",
      "RTE Removed",
      "Eligible unpaid students changed to NON-RTE. Click Update All to save."
    );
  };

  /* ==========================================================
     FEE CHANGE
  ========================================================== */

  const handleFeeChange = (
    studentIndex,
    feeIndex,
    value
  ) => {
    let finalAmount = num(
      String(value || "").replace(
        /[^0-9.]/g,
        ""
      )
    );

    setStudents((prev) =>
      prev.map(
        (student, sIndex) => {
          if (
            sIndex !==
            studentIndex
          ) {
            return student;
          }

          const updatedFees =
            student.fees.map(
              (fee, fIndex) => {
                if (
                  fIndex !==
                  feeIndex
                ) {
                  return fee;
                }

                const actual =
                  num(
                    fee.actual_amount
                  );

                const paid =
                  num(
                    fee.paid_amount
                  );

                if (
                  finalAmount <
                  paid
                ) {
                  finalAmount =
                    paid;
                }

                if (
                  finalAmount >
                  actual
                ) {
                  finalAmount =
                    actual;
                }

                const discount =
                  Math.max(
                    actual -
                      finalAmount,
                    0
                  );

                const due =
                  Math.max(
                    finalAmount -
                      paid,
                    0
                  );

                return {
                  ...fee,

                  final_amount:
                    finalAmount,

                  discount,

                  due_amount:
                    due,
                };
              }
            );

          return {
            ...student,

            fees:
              updatedFees,
          };
        }
      )
    );
  };

  /* ==========================================================
     PREVIOUS FEE
  ========================================================== */

  const handlePreviousFeeChange = (
    studentIndex,
    value
  ) => {
    const amount = num(value);

    setStudents((prev) =>
      prev.map(
        (student, index) =>
          index ===
          studentIndex
            ? {
                ...student,

                previous_session_due_fees:
                  amount,
              }
            : student
      )
    );
  };

  /* ==========================================================
     PREVIOUS FEE REMARK
  ========================================================== */

  const handlePreviousFeeRemarkChange =
    (
      studentIndex,
      value
    ) => {
      setStudents((prev) =>
        prev.map(
          (
            student,
            index
          ) =>
            index ===
            studentIndex
              ? {
                  ...student,

                  previous_session_due_fees_remark:
                    value,
                }
              : student
        )
      );
    };

  /* ==========================================================
     BUILD API PAYLOAD
  ========================================================== */

  const buildPayload = (
    student
  ) => {
    return {
      enrollment_id:
        student.id,

      student_batch_n_fee_id:
        student.id,

      rte_status:
        student.rte_status,

      discount_remark:
        student.rte_status ===
        "RTE"
          ? "Student RTE"
          : "",

      previous_session_due_fees:
        showPreviousFee
          ? num(
              student.previous_session_due_fees
            )
          : 0,

      previous_session_due_fees_remark:
        showPreviousFee
          ? student.previous_session_due_fees_remark ||
            ""
          : "",

      fees: student.fees.map(
        (fee) => ({
          id:
            fee.student_fee_id ||
            null,

          student_fee_id:
            fee.student_fee_id ||
            null,

          batch_fee_id:
            fee.batch_fee_id ||
            null,

          fee_type:
            fee.fee_type,

          actual_amount:
            num(
              fee.actual_amount
            ),

          final_amount:
            num(
              fee.final_amount
            ),
        })
      ),
    };
  };

  /* ==========================================================
     VALIDATE STUDENT
  ========================================================== */

  const validateStudent = (
    student
  ) => {
    const errors = [];

    const totalPaid =
      getTotalPaid(student);

    if (
      student.rte_status ===
        "RTE" &&
      totalPaid > 0
    ) {
      errors.push(
        "RTE cannot be applied because payment already exists."
      );
    }

    student.fees.forEach(
      (fee) => {
        const actual =
          num(
            fee.actual_amount
          );

        const paid =
          num(
            fee.paid_amount
          );

        const payable =
          num(
            fee.final_amount
          );

        if (
          fee.is_existing &&
          !fee.student_fee_id
        ) {
          errors.push(
            `${fee.fee_type}: Existing fee ID is missing.`
          );
        }

        if (
          payable < paid
        ) {
          errors.push(
            `${fee.fee_type}: Payable ₹${money(
              payable
            )} cannot be less than paid ₹${money(
              paid
            )}.`
          );
        }

        if (
          payable > actual
        ) {
          errors.push(
            `${fee.fee_type}: Payable fee cannot exceed actual fee.`
          );
        }

        if (actual < 0) {
          errors.push(
            `${fee.fee_type}: Actual fee cannot be negative.`
          );
        }

        if (paid < 0) {
          errors.push(
            `${fee.fee_type}: Paid fee cannot be negative.`
          );
        }
      }
    );

    return errors;
  };

  /* ==========================================================
     MARK STUDENT SAVED
  ========================================================== */

  const markStudentAsSaved = (
    student
  ) => {
    originalStudentsRef.current[
      student.id
    ] =
      createComparableStudent(
        student
      );
  };

  /* ==========================================================
     UPDATE SINGLE STUDENT
  ========================================================== */

  const updateStudent = async (
    studentIndex
  ) => {
    const student =
      students[studentIndex];

    if (!student) return;

    if (!isStudentDirty(student)) {
      return SweetAlertShow(
        "info",
        "No Changes",
        "There are no changes to save for this student."
      );
    }

    const errors =
      validateStudent(student);

    if (errors.length) {
      return SweetAlertShow(
        "error",
        "Validation Error",
        errors.join("\n")
      );
    }

    setUpdating(true);

    try {
      const payload =
        buildPayload(student);

      console.log(
        "UPDATE STUDENT PAYLOAD:",
        payload
      );

      const response =
        await studentBulkFeeUpdate(
          payload
        );

      if (
        response?.data?.status
      ) {
        markStudentAsSaved(
          student
        );

        setUpdateStatus(
          (prev) => ({
            ...prev,

            [studentIndex]: {
              status:
                "success",

              message:
                response
                  ?.data
                  ?.message ||
                "Updated successfully",
            },
          })
        );

        SweetAlertShow(
          "success",
          "Updated",
          `${
            student.student
              ?.name ||
            "Student"
          } updated successfully.`
        );

        if (
          response?.data?.data
        ) {
          const updatedStudent =
            prepareStudent(
              response.data.data
            );

          setStudents(
            (prev) =>
              prev.map(
                (
                  item,
                  index
                ) =>
                  index ===
                  studentIndex
                    ? updatedStudent
                    : item
              )
          );

          originalStudentsRef.current[
            updatedStudent.id
          ] =
            createComparableStudent(
              updatedStudent
            );
        }
      } else {
        throw new Error(
          response?.data
            ?.message ||
            "Update failed"
        );
      }
    } catch (error) {
      const message =
        error?.response
          ?.data
          ?.message ||
        error?.response
          ?.data
          ?.errors ||
        error?.message ||
        "Update failed";

      setUpdateStatus(
        (prev) => ({
          ...prev,

          [studentIndex]: {
            status: "error",

            message:
              Array.isArray(
                message
              )
                ? message.join(
                    ", "
                  )
                : String(
                    message
                  ),
          },
        })
      );

      SweetAlertShow(
        "error",
        "Update Failed",
        Array.isArray(
          message
        )
          ? message.join(
              ", "
            )
          : String(message)
      );
    } finally {
      setUpdating(false);
    }
  };

  /* ==========================================================
     UPDATE ALL CHANGED ONLY
  ========================================================== */

  const updateAll = async () => {
    if (!students.length) {
      return;
    }

    const changedStudents =
      students.filter(
        (student) =>
          isStudentDirty(student)
      );

    if (!changedStudents.length) {
      return SweetAlertShow(
        "info",
        "No Changes",
        "No student has any unsaved changes."
      );
    }

    const invalid = [];

    changedStudents.forEach(
      (student) => {
        const errors =
          validateStudent(
            student
          );

        if (errors.length) {
          invalid.push({
            student,

            errors,
          });
        }
      }
    );

    if (invalid.length) {
      const first =
        invalid[0];

      return SweetAlertShow(
        "error",
        "Validation Error",
        `${
          first.student
            .student?.name ||
          "Student"
        }: ${first.errors.join(
          ", "
        )}`
      );
    }

    setUpdating(true);

    const status = {};

    try {
      const chunkSize = 10;

      for (
        let i = 0;
        i <
        changedStudents.length;
        i += chunkSize
      ) {
        const chunk =
          changedStudents.slice(
            i,
            i + chunkSize
          );

        await Promise.allSettled(
          chunk.map(
            async (
              student
            ) => {
              const index =
                students.findIndex(
                  (item) =>
                    item.id ===
                    student.id
                );

              try {
                const payload =
                  buildPayload(
                    student
                  );

                console.log(
                  `UPDATE CHANGED STUDENT ${
                    index + 1
                  }`,
                  payload
                );

                const response =
                  await studentBulkFeeUpdate(
                    payload
                  );

                if (
                  response?.data
                    ?.status
                ) {
                  status[
                    index
                  ] = {
                    status:
                      "success",

                    message:
                      response
                        ?.data
                        ?.message ||
                      "Updated",
                  };

                  markStudentAsSaved(
                    student
                  );
                } else {
                  status[
                    index
                  ] = {
                    status:
                      "error",

                    message:
                      response
                        ?.data
                        ?.message ||
                      "Update failed",
                  };
                }
              } catch (error) {
                status[
                  index
                ] = {
                  status: "error",

                  message:
                    error
                      ?.response
                      ?.data
                      ?.message ||
                    error?.message ||
                    "Update failed",
                };
              }
            }
          )
        );

        setUpdateStatus({
          ...status,
        });
      }

      const success =
        Object.values(
          status
        ).filter(
          (x) =>
            x.status ===
            "success"
        ).length;

      const failed =
        Object.values(
          status
        ).filter(
          (x) =>
            x.status ===
            "error"
        ).length;

      SweetAlertShow(
        failed === 0
          ? "success"
          : "warning",
        "Update Completed",
        `Changed: ${changedStudents.length}, Updated: ${success}, Failed: ${failed}`
      );

      if (failed === 0) {
        await fetchStudents(
          batchId
        );
      }
    } finally {
      setUpdating(false);
    }
  };

  /* ==========================================================
     EXCEL RTE IMPORT
  ========================================================== */

  const handleExcelRteUpload = async (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) return;

    if (!students.length) {
      event.target.value = "";

      return SweetAlertShow(
        "warning",
        "Select Batch",
        "Please load students first."
      );
    }

    setExcelLoading(true);

    try {
      const buffer =
        await file.arrayBuffer();

      const workbook =
        XLSX.read(buffer, {
          type: "array",
        });

      const sheetName =
        workbook.SheetNames[0];

      const worksheet =
        workbook.Sheets[
          sheetName
        ];

      const rows =
        XLSX.utils.sheet_to_json(
          worksheet,
          {
            defval: "",
          }
        );

      if (!rows.length) {
        throw new Error(
          "Excel file is empty."
        );
      }

      const firstRow =
        rows[0];

      const registrationKey =
        Object.keys(
          firstRow
        ).find((key) => {
          const k =
            normalize(key).replace(
              /[\s_-]/g,
              ""
            );

          return [
            "registrationno",
            "registrationnumber",
            "registration",
            "regno",
            "admissionno",
          ].includes(k);
        });

      if (!registrationKey) {
        throw new Error(
          "Registration number column not found. Use 'registration_no' in Excel."
        );
      }

      const excelRegistrationNumbers =
        new Set();

      rows.forEach((row) => {
        const registration =
          normalizeRegistration(
            row[
              registrationKey
            ]
          );

        if (registration) {
          excelRegistrationNumbers.add(
            registration
          );
        }
      });

      let matched = 0;

      const notMatched = [];

      setStudents((prev) =>
        prev.map((student) => {
          const registration =
            normalizeRegistration(
              student.student
                ?.registration_no
            );

          const totalPaid =
            getTotalPaid(
              student
            );

          if (
            excelRegistrationNumbers.has(
              registration
            )
          ) {
            if (
              totalPaid > 0
            ) {
              notMatched.push(
                `${
                  student.student
                    ?.name ||
                  "Unknown"
                } - payment exists`
              );

              return student;
            }

            matched++;

            const updatedFees =
              student.fees.map(
                (fee) => {
                  const actual =
                    num(
                      fee.actual_amount
                    );

                  const paid =
                    num(
                      fee.paid_amount
                    );

                  if (
                    paid > 0
                  ) {
                    return fee;
                  }

                  return {
                    ...fee,

                    final_amount:
                      0,

                    discount:
                      actual,

                    due_amount:
                      0,
                  };
                }
              );

            return {
              ...student,

              rte_status:
                "RTE",

              fees:
                updatedFees,
            };
          }

          return student;
        })
      );

      const loadedRegistrations =
        new Set(
          students.map(
            (student) =>
              normalizeRegistration(
                student.student
                  ?.registration_no
              )
          )
        );

      excelRegistrationNumbers.forEach(
        (registration) => {
          if (
            !loadedRegistrations.has(
              registration
            )
          ) {
            notMatched.push(
              registration
            );
          }
        }
      );

      setExcelMatched(
        matched
      );

      setExcelNotMatched(
        notMatched
      );

      SweetAlertShow(
        matched > 0
          ? "success"
          : "warning",
        "Excel Processed",
        `${matched} students marked as RTE. Click Update All to save changes.`
      );
    } catch (error) {
      SweetAlertShow(
        "error",
        "Excel Error",
        error?.message ||
          "Unable to process Excel file."
      );
    } finally {
      setExcelLoading(false);

      event.target.value = "";
    }
  };

  /* ==========================================================
     MISSING BATCH FEE HELPERS
     ========================================================== */

  const availableBatchFees = useMemo(() => {
    const map = new Map();

    students.forEach((student) => {
      (student?.batch?.fee_details || []).forEach((fee) => {
        if (String(fee.status ?? "1") !== "1") return;
        const key = `${student.batch_id || student?.batch?.id || ""}-${fee.id}`;
        if (!map.has(key)) {
          map.set(key, {
            ...fee,
            batch_id: student.batch_id || student?.batch?.id || null,
            batch_title: student?.batch?.title || "-",
          });
        }
      });
    });

    return Array.from(map.values());
  }, [students]);

  const studentHasFeeType = (student, feeType) => {
    const key = normalizeFeeKey(feeType);
    return (student.fees || []).some(
      (fee) => normalizeFeeKey(fee.fee_type) === key
    );
  };

  const getMissingCountForBatchFee = (batchFee) => {
    return students.filter((student) => {
      const sameBatch =
        String(student.batch_id || student?.batch?.id || "") ===
        String(batchFee.batch_id || "");
      return sameBatch && !studentHasFeeType(student, batchFee.fee_type);
    }).length;
  };

  const totalMissingBatchFeeAssignments = useMemo(() => {
    return availableBatchFees.reduce(
      (sum, fee) => sum + getMissingCountForBatchFee(fee),
      0
    );
  }, [availableBatchFees, students]);

  const openBatchFeeModal = () => {
    if (!students.length) {
      return SweetAlertShow(
        "warning",
        "Select Batch",
        "Please load students first."
      );
    }

    const defaults = availableBatchFees
      .filter((fee) => getMissingCountForBatchFee(fee) > 0)
      .map((fee) => String(fee.id));

    setSelectedBatchFeeIds(defaults);
    setShowBatchFeeModal(true);
  };

  const toggleBatchFeeSelection = (feeId) => {
    const id = String(feeId);
    setSelectedBatchFeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const applyMissingBatchFeesToStudents = () => {
    if (!selectedBatchFeeIds.length) {
      return SweetAlertShow(
        "warning",
        "Select Fee",
        "Please select at least one batch fee type."
      );
    }

    setBatchFeeApplyLoading(true);

    let applied = 0;
    let affectedStudents = 0;

    setStudents((prev) =>
      prev.map((student) => {
        const studentBatchId = String(
          student.batch_id || student?.batch?.id || ""
        );

        const selectedForStudent = availableBatchFees.filter(
          (batchFee) =>
            String(batchFee.batch_id || "") === studentBatchId &&
            selectedBatchFeeIds.includes(String(batchFee.id)) &&
            !studentHasFeeType(student, batchFee.fee_type)
        );

        if (!selectedForStudent.length) return student;

        affectedStudents++;

        const newFees = selectedForStudent.map((batchFee) => {
          const actual = num(batchFee.amount);
          const isRte = String(student.rte_status).toUpperCase() === "RTE";
          applied++;

          return {
            student_fee_id: null,
            batch_fee_id: batchFee.id,
            fee_type: batchFee.fee_type,
            actual_amount: actual,
            paid_amount: 0,
            discount: isRte ? actual : 0,
            final_amount: isRte ? 0 : actual,
            due_amount: isRte ? 0 : actual,
            is_batch_fee: true,
            is_existing: false,
            is_new_from_batch: true,
          };
        });

        return {
          ...student,
          fees: [...(student.fees || []), ...newFees],
        };
      })
    );

    setBatchFeeApplyLoading(false);
    setShowBatchFeeModal(false);

    SweetAlertShow(
      "success",
      "Fees Prepared",
      `${applied} missing fee entries added for ${affectedStudents} students. Please verify the table, then click Update All to save.`
    );
  };

  const feeColumns = useMemo(() => {
    const map = new Map();
    students.forEach((student) => {
      (student.fees || []).forEach((fee) => {
        const key = normalizeFeeKey(fee.fee_type);
        if (!map.has(key)) map.set(key, fee.fee_type);
      });
    });
    return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
  }, [students]);

  /* ==========================================================
     BATCH TOTALS
  ========================================================== */

  const batchTotals = useMemo(() => {
    return students.reduce(
      (total, student) => {
        const t =
          calculateTotals(
            student
          );

        total.actual +=
          t.actual;

        total.paid +=
          t.paid;

        total.discount +=
          t.discount;

        total.payable +=
          t.payable;

        total.due += t.due;

        total.previous +=
          t.previousFee;

        return total;
      },
      {
        actual: 0,
        paid: 0,
        discount: 0,
        payable: 0,
        due: 0,
        previous: 0,
      }
    );
  }, [
    students,
    showPreviousFee,
  ]);

  /* ==========================================================
     RENDER
  ========================================================== */

  return (
    <>
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            background:
              "rgba(0,0,0,.25)",
            zIndex: 9999,
          }}
        >
          <div className="spinner-border text-primary" />
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">

                    {/* ==================================================
                        FILTER CARD
                    ================================================== */}

                    <div className="card mb-3">
                      <div className="card-body">

                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h5 className="mb-1">
                              Student Fee Update
                            </h5>

                            <small className="text-muted">
                              Update fees, RTE
                              status and
                              previous session
                              dues
                            </small>
                          </div>
                        </div>

                        <div className="row g-3">

                          {/* SESSION */}

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={
                                  sessionId
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleSessionChange(
                                    e
                                      .target
                                      .value
                                  )
                                }
                              >
                                <option value="">
                                  Select Session
                                </option>

                                {sessions.map(
                                  (
                                    session
                                  ) => (
                                    <option
                                      key={
                                        session.id
                                      }
                                      value={
                                        session.id
                                      }
                                    >
                                      {
                                        session.session
                                      }

                                      {String(
                                        session.status
                                      ) ===
                                      "1"
                                        ? " (Active)"
                                        : ""}
                                    </option>
                                  )
                                )}
                              </select>

                              <label>
                                Session
                              </label>
                            </div>
                          </div>

                          {/* BATCH */}

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={
                                  batchId
                                }
                                onChange={(
                                  e
                                ) =>
                                  handleBatchChange(
                                    e
                                      .target
                                      .value
                                  )
                                }
                                disabled={
                                  !sessionId
                                }
                              >
                                <option value="">
                                  Select Batch
                                </option>

                                <option value="all">
                                  All Batches
                                </option>

                                {batches.map(
                                  (
                                    batch
                                  ) => (
                                    <option
                                      key={
                                        batch.id
                                      }
                                      value={
                                        batch.id
                                      }
                                    >
                                      {
                                        batch.title
                                      }

                                      {batch
                                        .course
                                        ?.title
                                        ? ` (${batch.course.title})`
                                        : ""}
                                    </option>
                                  )
                                )}
                              </select>

                              <label>
                                Batch
                              </label>
                            </div>
                          </div>

                          {/* ==================================================
                              STUDENT SEARCH
                          ================================================== */}

                          <div className="col-md-4">
                            <div className="form-floating position-relative">

                              <input
                                type="text"
                                className="form-control pe-5"
                                placeholder="Search Student"
                                value={
                                  studentSearch
                                }
                                onChange={(
                                  e
                                ) =>
                                  setStudentSearch(
                                    e
                                      .target
                                      .value
                                  )
                                }
                                disabled={
                                  !students.length
                                }
                                autoComplete="off"
                              />

                              <label>
                                Student
                              </label>

                              {/* SEARCH ICON */}

                              {!studentSearch && (
                                <i
                                  className="ri-search-line position-absolute"
                                  style={{
                                    right:
                                      "15px",
                                    top:
                                      "50%",
                                    transform:
                                      "translateY(-50%)",
                                    fontSize:
                                      "20px",
                                    pointerEvents:
                                      "none",
                                    color:
                                      "#6c757d",
                                  }}
                                />
                              )}

                              {/* CLEAR ICON */}

                              {studentSearch && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link position-absolute p-0"
                                  onClick={
                                    clearStudentSearch
                                  }
                                  style={{
                                    right:
                                      "15px",
                                    top:
                                      "50%",
                                    transform:
                                      "translateY(-50%)",
                                    textDecoration:
                                      "none",
                                  }}
                                >
                                  <i className="ri-close-circle-fill fs-5 text-secondary" />
                                </button>
                              )}
                            </div>

                            {/* SEARCH HELP */}

                            {students.length >
                              0 && (
                              <div className="mt-1">
                                <small className="text-muted">
                                  Search by
                                  name,
                                  mobile,
                                  roll no
                                  or
                                  registration
                                  no
                                </small>
                              </div>
                            )}
                          </div>

                          {/* STUDENT COUNT */}

                          <div className="col-md-2">
                            <div className="card bg-light border mb-0 h-100">
                              <div className="card-body py-2">
                                <small className="text-muted">
                                  Students
                                </small>

                                <h4 className="mb-0">
                                  {studentSearch
                                    ? `${filteredStudents.length} / ${students.length}`
                                    : students.length}
                                </h4>

                                {dirtyStudents.length >
                                  0 && (
                                  <small className="text-warning">
                                    {
                                      dirtyStudents.length
                                    }{" "}
                                    changed
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* UPDATE ALL */}

                          <div className="col-md-3 d-flex align-items-center justify-content-end">
                            <button
                              className="btn btn-success btn-lg"
                              onClick={
                                updateAll
                              }
                              disabled={
                                updating ||
                                !dirtyStudents.length
                              }
                            >
                              {updating ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" />
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <i className="ri-save-3-line me-1" />

                                  Update All

                                  {dirtyStudents.length >
                                    0 &&
                                    ` (${dirtyStudents.length})`}
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {/* ==================================================
                            SEARCH RESULT INFO
                        ================================================== */}

                        {studentSearch && (
                          <div className="alert alert-primary mt-3 mb-0 py-2">
                            <i className="ri-search-line me-1" />

                            Searching for{" "}
                            <strong>
                              "{studentSearch}"
                            </strong>

                            {" — "}

                            <strong>
                              {
                                filteredStudents.length
                              }
                            </strong>{" "}
                            student
                            {filteredStudents.length !==
                            1
                              ? "s"
                              : ""}{" "}
                            found.

                            {filteredStudents.length ===
                              0 && (
                              <span className="ms-2">
                                No student
                                matches
                                your search.
                              </span>
                            )}
                          </div>
                        )}

                        {/* ==================================================
                            BULK RTE CONTROLS
                        ================================================== */}

                        <hr />

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <strong>
                            Bulk RTE:
                          </strong>

                          <button
                            className="btn btn-outline-success"
                            onClick={
                              markAllRTE
                            }
                            disabled={
                              !students.length ||
                              updating
                            }
                          >
                            <i className="ri-check-double-line me-1" />

                            Mark All RTE
                          </button>

                          <button
                            className="btn btn-outline-secondary"
                            onClick={
                              markAllNonRTE
                            }
                            disabled={
                              !students.length ||
                              updating
                            }
                          >
                            <i className="ri-close-circle-line me-1" />

                            Mark All NON-RTE
                          </button>

                          <button
                            className="btn btn-outline-primary"
                            onClick={() =>
                              excelInputRef.current?.click()
                            }
                            disabled={
                              !students.length ||
                              excelLoading ||
                              updating
                            }
                          >
                            {excelLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" />

                                Processing Excel...
                              </>
                            ) : (
                              <>
                                <i className="ri-file-excel-2-line me-1" />

                                Import RTE Excel
                              </>
                            )}
                          </button>

                          <input
                            ref={
                              excelInputRef
                            }
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="d-none"
                            onChange={
                              handleExcelRteUpload
                            }
                          />

                          <button
                            type="button"
                            className="btn btn-outline-warning"
                            onClick={openBatchFeeModal}
                            disabled={!students.length || updating}
                          >
                            <i className="ri-refresh-line me-1" />
                            Apply Missing Batch Fees
                            {totalMissingBatchFeeAssignments > 0
                              ? ` (${totalMissingBatchFeeAssignments})`
                              : ""}
                          </button>

                          <small className="text-muted">
                            Excel must contain
                            a{" "}
                            <strong>
                              registration_no
                            </strong>{" "}
                            column.
                          </small>
                        </div>

                        {/* EXCEL RESULT */}

                        {excelMatched >
                          0 && (
                          <div className="alert alert-success mt-3 mb-0">
                            <strong>
                              Excel:
                            </strong>{" "}
                            {
                              excelMatched
                            }{" "}
                            students matched
                            and marked RTE.
                            Click{" "}
                            <strong>
                              Update All
                            </strong>{" "}
                            to save.
                          </div>
                        )}

                        {excelNotMatched.length >
                          0 && (
                          <div className="alert alert-warning mt-3 mb-0">
                            <strong>
                              Not matched /
                              skipped:
                            </strong>{" "}
                            {
                              excelNotMatched.length
                            }{" "}
                            students.

                            <div
                              className="mt-2"
                              style={{
                                maxHeight:
                                  "100px",
                                overflowY:
                                  "auto",
                              }}
                            >
                              {excelNotMatched
                                .slice(
                                  0,
                                  50
                                )
                                .join(
                                  ", "
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ==================================================
                        SUMMARY
                    ================================================== */}

                    {students.length >
                      0 && (
                      <div className="row g-2 mb-3">
                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">
                                Actual Fees
                              </small>

                              <h6 className="mb-0">
                                ₹
                                {money(
                                  batchTotals.actual
                                )}
                              </h6>
                            </div>
                          </div>
                        </div>

                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">
                                Paid
                              </small>

                              <h6 className="mb-0 text-success">
                                ₹
                                {money(
                                  batchTotals.paid
                                )}
                              </h6>
                            </div>
                          </div>
                        </div>

                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">
                                Discount
                              </small>

                              <h6 className="mb-0 text-warning">
                                ₹
                                {money(
                                  batchTotals.discount
                                )}
                              </h6>
                            </div>
                          </div>
                        </div>

                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">
                                Payable
                              </small>

                              <h6 className="mb-0">
                                ₹
                                {money(
                                  batchTotals.payable
                                )}
                              </h6>
                            </div>
                          </div>
                        </div>

                        <div className="col">
                          <div className="card border">
                            <div className="card-body py-2">
                              <small className="text-muted">
                                Due
                              </small>

                              <h6 className="mb-0 text-danger">
                                ₹
                                {money(
                                  batchTotals.due
                                )}
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ==================================================
                        STUDENT TABLE
                    ================================================== */}

                    <div className="card">
                      <div className="card-header d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-0">
                            Students Fee
                            Details
                          </h5>

                          {batchId && (
                            <small className="text-muted">
                              {batchId ===
                              "all"
                                ? "All Batches"
                                : batches.find(
                                    (
                                      b
                                    ) =>
                                      String(
                                        b.id
                                      ) ===
                                      String(
                                        batchId
                                      )
                                  )
                                    ?.title}
                            </small>
                          )}
                        </div>

                        {students.length >
                          0 && (
                          <div className="d-flex gap-2">

                            <span className="badge bg-primary">
                              {studentSearch
                                ? `${filteredStudents.length} / ${students.length}`
                                : students.length}{" "}
                              Students
                            </span>

                            {dirtyStudents.length >
                              0 && (
                              <span className="badge bg-warning text-dark">
                                {
                                  dirtyStudents.length
                                }{" "}
                                Unsaved
                              </span>
                            )}

                          </div>
                        )}
                      </div>

                      <div className="card-body p-0">

                        {!students.length ? (
                          <div className="text-center py-5">
                            <i
                              className="ri-file-list-3-line text-muted"
                              style={{
                                fontSize:
                                  "50px",
                              }}
                            />

                            <h5 className="mt-3">
                              Select Session
                              & Batch
                            </h5>

                            <p className="text-muted mb-0">
                              Students will
                              automatically
                              appear here.
                            </p>
                          </div>
                        ) : filteredStudents.length ===
                          0 ? (
                          /*
                           ==================================================
                           NO SEARCH RESULTS
                           ==================================================
                          */

                          <div className="text-center py-5">
                            <i
                              className="ri-user-search-line text-muted"
                              style={{
                                fontSize:
                                  "50px",
                              }}
                            />

                            <h5 className="mt-3">
                              No Student
                              Found
                            </h5>

                            <p className="text-muted mb-3">
                              No student matches
                              "{studentSearch}".
                            </p>

                            <button
                              className="btn btn-outline-primary"
                              onClick={
                                clearStudentSearch
                              }
                            >
                              <i className="ri-close-line me-1" />

                              Clear Search
                            </button>
                          </div>
                        ) : (
                          <div
                            className="table-responsive"
                            style={{
                              maxHeight:
                                "calc(100vh - 300px)",
                            }}
                          >
                            <table className="table table-bordered table-hover align-middle mb-0">

                              <thead
                                className="table-dark"
                                style={{
                                  position:
                                    "sticky",
                                  top: 0,
                                  zIndex: 10,
                                }}
                              >
                                <tr>

                                  <th>
                                    #
                                  </th>

                                  <th
                                    style={{
                                      minWidth:
                                        250,
                                    }}
                                  >
                                    Student
                                  </th>

                                  <th>
                                    Batch
                                  </th>

                                  <th
                                    style={{
                                      minWidth:
                                        130,
                                    }}
                                  >
                                    RTE
                                  </th>

                                  {feeColumns.map((column) => (
                                    <th
                                      key={column.key}
                                      style={{ minWidth: 230 }}
                                    >
                                      {column.label}
                                    </th>
                                  ))}

                                  <th
                                    style={{
                                      minWidth:
                                        180,
                                    }}
                                  >
                                    Total
                                  </th>

                                  <th>
                                    Action
                                  </th>

                                </tr>
                              </thead>

                              <tbody>

                                {filteredStudents.map(
                                  ({
                                    student,
                                    originalIndex,
                                  }) => {

                                    /*
                                     IMPORTANT:

                                     originalIndex is used for every
                                     update/change function.

                                     Therefore filtering does NOT
                                     break the existing functionality.
                                    */

                                    const studentIndex =
                                      originalIndex;

                                    const totals =
                                      calculateTotals(
                                        student
                                      );

                                    const totalPaid =
                                      totals.paid;

                                    const dirty =
                                      isStudentDirty(
                                        student
                                      );

                                    const status =
                                      updateStatus[
                                        studentIndex
                                      ];

                                    return (
                                      <tr
                                        key={
                                          student.id
                                        }
                                        className={
                                          dirty
                                            ? "table-warning"
                                            : ""
                                        }
                                      >

                                        {/* SN */}

                                        <td>
                                          <strong>
                                            {studentIndex +
                                              1}
                                          </strong>

                                          {dirty && (
                                            <span
                                              className="badge bg-warning text-dark d-block mt-1"
                                              style={{
                                                fontSize:
                                                  "9px",
                                              }}
                                            >
                                              UNSAVED
                                            </span>
                                          )}
                                        </td>

                                        {/* STUDENT */}

                                        <td>

                                          <div className="fw-semibold">
                                            {student
                                              .student
                                              ?.name ||
                                              "-"}
                                          </div>

                                          <div className="small text-muted">
                                            Reg:{" "}
                                            {student
                                              .student
                                              ?.registration_no ||
                                              "-"}
                                          </div>

                                          <div className="small text-muted">
                                            Mobile:{" "}
                                            {student
                                              .student
                                              ?.phone ||
                                              "-"}
                                          </div>

                                          <div className="small text-muted">
                                            Roll No:{" "}
                                            <strong>
                                              {student
                                                ?.roll_number ||
                                                "-"}
                                            </strong>
                                          </div>

                                          {totalPaid >
                                            0 && (
                                            <span className="badge bg-success mt-1">
                                              Paid ₹
                                              {money(
                                                totalPaid
                                              )}
                                            </span>
                                          )}

                                        </td>

                                        {/* BATCH */}

                                        <td>
                                          <span className="badge bg-light text-dark border">
                                            {student
                                              .batch
                                              ?.title ||
                                              "-"}
                                          </span>
                                        </td>

                                        {/* RTE */}

                                        <td>

                                          <select
                                            className={`form-select form-select-sm ${
                                              student.rte_status ===
                                              "RTE"
                                                ? "border-success bg-success-subtle"
                                                : ""
                                            }`}
                                            value={
                                              student.rte_status
                                            }
                                            onChange={(
                                              e
                                            ) =>
                                              handleRteChange(
                                                studentIndex,
                                                e
                                                  .target
                                                  .value
                                              )
                                            }
                                            disabled={
                                              totalPaid >
                                              0
                                            }
                                          >
                                            <option value="NON-RTE">
                                              NON-RTE
                                            </option>

                                            <option value="RTE">
                                              RTE
                                            </option>
                                          </select>

                                          {totalPaid >
                                            0 && (
                                            <small className="text-danger d-block mt-1">
                                              Locked:
                                              payment
                                              exists
                                            </small>
                                          )}

                                        </td>

                                        {/* FEES - ONLY STUDENT APPLIED FEES */}

                                        {feeColumns.map((column) => {
                                          const feeIndex = (student.fees || []).findIndex(
                                            (fee) =>
                                              normalizeFeeKey(fee.fee_type) === column.key
                                          );

                                          if (feeIndex === -1) {
                                            return (
                                              <td
                                                key={`${student.id}-${column.key}`}
                                                className="bg-light"
                                              >
                                                <div className="text-center text-muted py-3">
                                                  <i className="ri-information-line d-block mb-1" />
                                                  <small>Not applied</small>
                                                </div>
                                              </td>
                                            );
                                          }

                                          const fee = student.fees[feeIndex];
                                          const actual = num(fee.actual_amount);
                                          const paid = num(fee.paid_amount);
                                          const discount = num(fee.discount);
                                          const payable = num(fee.final_amount);
                                          const due = num(fee.due_amount);
                                          const locked = paid > 0;

                                          return (
                                            <td
                                              key={`${student.id}-${column.key}`}
                                              className={locked ? "bg-light" : ""}
                                            >
                                              {fee.is_new_from_batch && (
                                                <span className="badge bg-warning text-dark mb-2">
                                                  NEW - NOT SAVED
                                                </span>
                                              )}

                                              <div className="small mb-1">
                                                <div className="d-flex justify-content-between">
                                                  <span>Actual</span>
                                                  <strong>₹{money(actual)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between text-success">
                                                  <span>Paid</span>
                                                  <strong>₹{money(paid)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between text-warning">
                                                  <span>Discount</span>
                                                  <strong>₹{money(discount)}</strong>
                                                </div>
                                              </div>

                                              <div className="input-group input-group-sm">
                                                <span className="input-group-text">₹</span>
                                                <input
                                                  type="number"
                                                  min={paid}
                                                  max={actual}
                                                  step="0.01"
                                                  className="form-control fw-semibold"
                                                  value={payable}
                                                  disabled={
                                                    student.rte_status === "RTE" || locked
                                                  }
                                                  onChange={(e) =>
                                                    handleFeeChange(
                                                      studentIndex,
                                                      feeIndex,
                                                      e.target.value
                                                    )
                                                  }
                                                />
                                              </div>

                                              <div className="d-flex justify-content-between mt-1">
                                                <small className="text-muted">Payable</small>
                                                <strong>₹{money(payable)}</strong>
                                              </div>
                                              <div className="d-flex justify-content-between">
                                                <small className="text-danger">Due</small>
                                                <strong className="text-danger">
                                                  ₹{money(due)}
                                                </strong>
                                              </div>

                                              {fee.student_fee_id && (
                                                <small
                                                  className="text-muted d-block mt-1"
                                                  style={{ fontSize: "10px" }}
                                                >
                                                  Fee ID: {fee.student_fee_id}
                                                </small>
                                              )}

                                              {locked && (
                                                <small className="text-muted d-block">
                                                  Paid fee locked
                                                </small>
                                              )}
                                            </td>
                                          );
                                        })}

                                        {/* TOTAL */}

                                        <td>

                                          <div className="small">

                                            <div className="d-flex justify-content-between">
                                              <span>
                                                Actual
                                              </span>

                                              <strong>
                                                ₹
                                                {money(
                                                  totals.actual
                                                )}
                                              </strong>
                                            </div>

                                            <div className="d-flex justify-content-between text-success">
                                              <span>
                                                Paid
                                              </span>

                                              <strong>
                                                ₹
                                                {money(
                                                  totals.paid
                                                )}
                                              </strong>
                                            </div>

                                            <div className="d-flex justify-content-between text-warning">
                                              <span>
                                                Discount
                                              </span>

                                              <strong>
                                                ₹
                                                {money(
                                                  totals.discount
                                                )}
                                              </strong>
                                            </div>

                                            <hr className="my-1" />

                                            <div className="d-flex justify-content-between">
                                              <span>
                                                Payable
                                              </span>

                                              <strong>
                                                ₹
                                                {money(
                                                  totals.payable
                                                )}
                                              </strong>
                                            </div>

                                            <div className="d-flex justify-content-between text-danger">
                                              <span>
                                                Due
                                              </span>

                                              <strong>
                                                ₹
                                                {money(
                                                  totals.due
                                                )}
                                              </strong>
                                            </div>

                                          </div>

                                        </td>

                                        {/* ACTION */}

                                        <td>

                                          <button
                                            className={`btn btn-sm w-100 ${
                                              dirty
                                                ? "btn-primary"
                                                : "btn-outline-secondary"
                                            }`}
                                            onClick={() =>
                                              updateStudent(
                                                studentIndex
                                              )
                                            }
                                            disabled={
                                              updating ||
                                              !dirty
                                            }
                                          >
                                            <i className="ri-save-line me-1" />

                                            {dirty
                                              ? "Update"
                                              : "Saved"}
                                          </button>

                                          {status && (
                                            <div className="mt-2">

                                              {status.status ===
                                              "success" ? (
                                                <span className="badge bg-success">
                                                  Updated
                                                </span>
                                              ) : (
                                                <>
                                                  <span className="badge bg-danger">
                                                    Failed
                                                  </span>

                                                  <div
                                                    className="text-danger mt-1"
                                                    style={{
                                                      fontSize:
                                                        "11px",
                                                    }}
                                                  >
                                                    {
                                                      status.message
                                                    }
                                                  </div>
                                                </>
                                              )}

                                            </div>
                                          )}

                                        </td>

                                      </tr>
                                    );
                                  }
                                )}

                              </tbody>

                            </table>
                          </div>
                        )}

                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showBatchFeeModal && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ background: "rgba(0,0,0,.55)", zIndex: 10050 }}
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title mb-1">
                      Apply Missing Batch Fees to Students
                    </h5>
                    <small className="text-muted">
                      This only adds fee types that are present in the batch but
                      missing from the student's fee profile. Existing student
                      fees are never replaced by this action.
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowBatchFeeModal(false)}
                    disabled={batchFeeApplyLoading}
                  />
                </div>

                <div className="modal-body">
                  <div className="alert alert-warning">
                    <strong>Verification required:</strong> Select the fee types
                    below. Clicking <strong>Apply to Table</strong> does not save
                    directly to the server. It prepares the missing student fees
                    in this table. Review them and then use
                    <strong> Update All</strong> to save through the existing API.
                  </div>

                  {!availableBatchFees.length ? (
                    <div className="text-center py-5 text-muted">
                      No active batch fee structure found.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: 60 }}>Apply</th>
                            <th>Batch</th>
                            <th>Fee Type</th>
                            <th className="text-end">Batch Amount</th>
                            <th className="text-center">Missing Students</th>
                            <th className="text-center">Already Applied</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableBatchFees.map((fee) => {
                            const missing = getMissingCountForBatchFee(fee);
                            const batchStudents = students.filter(
                              (student) =>
                                String(
                                  student.batch_id || student?.batch?.id || ""
                                ) === String(fee.batch_id || "")
                            ).length;
                            const already = Math.max(batchStudents - missing, 0);
                            const checked = selectedBatchFeeIds.includes(
                              String(fee.id)
                            );

                            return (
                              <tr key={`${fee.batch_id}-${fee.id}`}>
                                <td className="text-center">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={checked}
                                    disabled={missing === 0}
                                    onChange={() =>
                                      toggleBatchFeeSelection(fee.id)
                                    }
                                  />
                                </td>
                                <td>{fee.batch_title}</td>
                                <td>
                                  <strong>{fee.fee_type}</strong>
                                </td>
                                <td className="text-end">
                                  ₹{money(fee.amount)}
                                </td>
                                <td className="text-center">
                                  <span
                                    className={`badge ${
                                      missing > 0 ? "bg-warning text-dark" : "bg-success"
                                    }`}
                                  >
                                    {missing}
                                  </span>
                                </td>
                                <td className="text-center">{already}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="modal-footer d-flex justify-content-between">
                  <div className="text-muted">
                    Missing assignments: <strong>{totalMissingBatchFeeAssignments}</strong>
                  </div>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowBatchFeeModal(false)}
                      disabled={batchFeeApplyLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={applyMissingBatchFeesToStudents}
                      disabled={
                        batchFeeApplyLoading || !selectedBatchFeeIds.length
                      }
                    >
                      {batchFeeApplyLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Applying...
                        </>
                      ) : (
                        <>
                          <i className="ri-check-double-line me-1" />
                          Apply to Table & Verify
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}